import type { ForwardRefExoticComponent } from 'react';
import { Component as ReactComponent, createRef } from 'react';
import isEqual from 'react-fast-compare';
import type { ScrollRestoreState } from './withScrollRestore.interface';
import scrollStorage from './withScrollRestore.store';

const DEFAULT_STORAGE_KEY = 'scroll-save';
const TIME_TO_LOAD_PAGE_MS = 300; /* Time to wait for page load */

function _awaitUserScroll(reference: HTMLElement, callback: () => void) {
    const onScroll = function () {
        reference.removeEventListener('scroll', onScroll);
        callback();
    };

    reference.addEventListener('scroll', onScroll);
}

function withScrollRestore<T extends HTMLElement, P extends object>(
    Component: ForwardRefExoticComponent<P>,
) {
    class WithScrollRestore extends ReactComponent<P & { saveKey: string }, ScrollRestoreState> {
        static displayName = `withScrollRestore(${Component.displayName ?? ''})`;

        constructor(properties: P & { saveKey: string }) {
            super(properties);
            this.state = {
                didSetInitialPosition: false,
                refComponent: createRef<T>(),
                positionSetTimeout: null,
                didScroll: false,
                storage: scrollStorage(properties.saveKey),
            };
        }

        updateState(stateNew: Partial<ScrollRestoreState>) {
            this.setState({
                ...this.state,
                ...stateNew,
            });
        }

        storageKey() {
            return this.state.storage.id;
        }

        setStorageKey(key: string) {
            if (this.state.storage.id !== key) {
                this.updateState({ storage: scrollStorage(key || DEFAULT_STORAGE_KEY) });
            }
        }

        override componentDidMount() {
            const reference = this.state.refComponent.current;

            if (!reference) {
                return;
            }

            const triggerUpdate = this.updateState.bind(this);

            const position = this.state.storage.get();
            reference.scroll(position.left, position.top);

            triggerUpdate({
                didSetInitialPosition: true,
                positionSetTimeout: null,
            });

            setTimeout(() => {
                _awaitUserScroll(reference, () => {
                    triggerUpdate({ didScroll: true });
                });
            }, TIME_TO_LOAD_PAGE_MS); /* Wait for the rest of the page to load, before we start checking for user movement */
        }

        override componentWillUnmount() {
            const reference = this.state.refComponent.current;
            if (!reference) {
                return;
            }
            if (!this.state.didScroll) {
                return;
            }

            /* Only update the position saved, if the user scrolled, otherwise we may be in a position, where the saved-scroll-position was loaded, but not yet applied */
            this.state.storage.set({
                top: reference.scrollTop,
                left: reference.scrollLeft,
            });

            if (this.state.positionSetTimeout) {
                clearTimeout(this.state.positionSetTimeout);
            }
        }

        override shouldComponentUpdate(nextProperties: P, nextState: ScrollRestoreState): boolean {
            const needsRerender =
                !isEqual(nextProperties, this.props) ||
                nextState.refComponent !== this.state.refComponent;

            return needsRerender;
        }

        override render() {
            const properties = this.props;
            const { refComponent } = this.state;

            return <Component ref={refComponent} {...properties} />;
        }
    }

    return WithScrollRestore;
}

export default withScrollRestore;
